import {
  type Observable,
  defer,
  finalize,
  from,
  shareReplay,
  switchMap,
} from 'rxjs';

import { HttpAgent, type HttpAgentConfig } from '@ag-ui/client';
import type { BaseEvent, RunAgentInput } from '@ag-ui/core';

type GetAuthToken = () => Promise<string | null | undefined>;
type RequestType = 'chat' | 'connect';

interface ConnectableHttpAgentConfig extends HttpAgentConfig {
  /**
   * Optional async callback returning a Bearer token. When provided, the
   * token is fetched fresh per request and merged into `this.headers` as
   * `Authorization: Bearer <token>` before the underlying POST is sent.
   * Use Auth0's `getAccessTokenSilently` (or equivalent) — it handles
   * silent refresh internally, so calling per-request is cheap.
   */
  getAuthToken?: GetAuthToken;
}

/**
 * HttpAgent subclass that implements connect() and supports per-request
 * Bearer token injection.
 *
 * connect(): The base HttpAgent.connect() throws ConnectNotImplementedError,
 * which CopilotKit silently catches — so no reconnect request is ever sent
 * on page load/reload. This subclass delegates connect() to run(), which
 * sends the same POST request. The backend's empty-query detection triggers
 * the checkpointer-based reconnect path, restoring state.
 *
 * Uses shareReplay to deduplicate concurrent connect calls (React
 * StrictMode double-mount and CopilotKit internals can fire connect()
 * multiple times for the same threadId).
 *
 * Auth: When `getAuthToken` is supplied, run() fetches a fresh token per
 * subscription and merges it into `this.headers` before delegating to
 * super.run(). Spreading preserves any other headers CopilotKit injected
 * via the provider's `headers` prop (e.g. X-CLIENT-ID) — those are written
 * to `this.headers` by `cloneForThread` in @copilotkit/react-core.
 *
 * clone() is overridden so the cloned (per-thread) instance keeps the
 * `getAuthToken` callback. AbstractAgent.clone() only copies a fixed
 * allowlist of fields, so any custom field would otherwise be lost.
 */
export class ConnectableHttpAgent extends HttpAgent {
  getAuthToken?: GetAuthToken;

  private _connectCache?: Map<string, Observable<BaseEvent>>;

  constructor(config: ConnectableHttpAgentConfig) {
    super(config);
    this.getAuthToken = config.getAuthToken;
  }

  override clone(): this {
    const cloned = super.clone() as this;
    cloned.getAuthToken = this.getAuthToken;
    // _connectCache is intentionally not copied — clones are per-thread
    // and start with a fresh dedup cache.
    return cloned;
  }

  override run(input: RunAgentInput): Observable<BaseEvent> {
    if (!this.getAuthToken) return super.run(input);

    return defer(() =>
      from(this._applyAuthHeader()).pipe(switchMap(() => super.run(input))),
    );
  }

  private async _applyAuthHeader(): Promise<void> {
    try {
      const token = await this.getAuthToken?.();
      if (token) {
        this.headers = {
          ...this.headers,
          Authorization: `Bearer ${token}`,
        };
      }
    } catch (error) {
      console.error('ConnectableHttpAgent: failed to fetch auth token', error);
    }
  }

  protected connect(input: RunAgentInput): Observable<BaseEvent> {
    const requestType: RequestType = 'connect';
    (input as { metadata?: { requestType: RequestType } }).metadata = {
      ...(input as { metadata?: object }).metadata,
      requestType,
    };

    if (!this._connectCache) this._connectCache = new Map();
    const key = input.threadId ?? '';

    const cached = this._connectCache.get(key);
    if (cached) return cached;

    const shared$ = this.run(input).pipe(
      shareReplay({ bufferSize: 100, refCount: true }),
      finalize(() => this._connectCache?.delete(key)),
    );

    this._connectCache.set(key, shared$);
    return shared$;
  }
}
